import { Controller, useWatch } from "react-hook-form";
import { Trash2, Wrench } from "lucide-react";
import { z } from "zod";
import { Input } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";

export const lineItemSchema = z
  .object({
    is_custom: z.boolean(),
    line_item_type: z.string().optional(),
    custom_name: z.string().optional(),
    custom_unit: z.string().optional(),
    custom_unit_rate: z.coerce.number().optional(),
    quantity: z.coerce.number().positive("Ilość musi być > 0"),
    description: z.string().optional(),
    attachment_required: z.boolean(),
    required_file_extension: z.string().optional(),
    result_files_expected: z.boolean(),
    /** Ustawione = wiersz pozycji automatycznej dopisany przy tej pozycji wyzwalającej. */
    auto_trigger: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.is_custom) {
      if (!val.custom_name?.trim())
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Podaj nazwę pozycji", path: ["custom_name"] });
      if (!val.custom_unit?.trim())
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Podaj jednostkę miary", path: ["custom_unit"] });
      if (!val.custom_unit_rate || val.custom_unit_rate <= 0)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Podaj cenę jednostkową > 0", path: ["custom_unit_rate"] });
    } else {
      if (!val.line_item_type)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Wybierz pozycję realizacyjną", path: ["line_item_type"] });
    }
  });

export interface LineItemFormRowProps {
  index: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any;
  lineItemOptions: { value: string; label: string }[];
  contractorId: string | undefined;
  onRemove: () => void;
  lineItemAttachmentMap?: Record<string, boolean>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue?: (name: any, value: any) => void;
}

type AnyErrs = Record<string, { message?: string } | undefined>;

function StandardRow({ index, control, register, errors, lineItemOptions, contractorId, onRemove, lineItemAttachmentMap, setValue }: LineItemFormRowProps) {
  const errs = ((errors?.line_items?.[index] ?? {}) as AnyErrs);
  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-800/50">
      <div className="flex items-start gap-3">
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            {/* Controller (not register): SearchableSelect only reflects values passed
                via the value prop, so reset()-populated data needs a controlled field */}
            <Controller
              control={control}
              name={`line_items.${index}.line_item_type`}
              render={({ field }) => (
                <SearchableSelect
                  label="Pozycja realizacyjna"
                  placeholder={
                    !contractorId
                      ? "— najpierw wybierz podwykonawcę —"
                      : lineItemOptions.length === 0
                      ? "Brak pozycji w umowach ramowych"
                      : "— wybierz pozycję —"
                  }
                  options={lineItemOptions}
                  error={errs.line_item_type?.message}
                  disabled={!contractorId || lineItemOptions.length === 0}
                  name={field.name}
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (lineItemAttachmentMap && setValue) {
                      const attachReq = lineItemAttachmentMap[e.target.value];
                      if (attachReq !== undefined) {
                        setValue(`line_items.${index}.attachment_required`, attachReq);
                      }
                    }
                  }}
                />
              )}
            />
          </div>
          <Input
            label="Ilość"
            type="number"
            step="any"
            error={errs.quantity?.message}
            {...register(`line_items.${index}.quantity`)}
          />
          <Input
            label="Opis pozycji (opcjonalny)"
            {...register(`line_items.${index}.description`)}
          />
          <div className="flex items-center gap-2 col-span-2">
            <input
              type="checkbox"
              id={`att_${index}`}
              {...register(`line_items.${index}.attachment_required`)}
              className="rounded"
            />
            <label htmlFor={`att_${index}`} className="text-sm text-gray-700 dark:text-gray-300">
              Załącznik obowiązkowy przy POCK
            </label>
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <input
              type="checkbox"
              id={`resfiles_${index}`}
              {...register(`line_items.${index}.result_files_expected`)}
              className="rounded"
            />
            <label htmlFor={`resfiles_${index}`} className="text-sm text-gray-700 dark:text-gray-300">
              Pliki pozycji realizacyjnej
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Wynikiem realizacji są pliki przesyłane przez podwykonawcę w zamówieniu
              </span>
            </label>
          </div>
        </div>
        <button type="button" onClick={onRemove} className="mt-6 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

function CustomRow({ index, register, errors, onRemove }: LineItemFormRowProps) {
  const errs = ((errors?.line_items?.[index] ?? {}) as AnyErrs);
  return (
    <div className="p-4 border border-amber-300 rounded-lg bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
      <div className="flex items-center gap-2 mb-3">
        <Wrench size={14} className="text-amber-600 dark:text-amber-400" />
        <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide">
          Pozycja niestandardowa
        </span>
      </div>
      <div className="flex items-start gap-3">
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Input
              label="Nazwa pozycji"
              placeholder="np. Pomiary geodezyjne, Projekt zamienny"
              error={errs.custom_name?.message}
              {...register(`line_items.${index}.custom_name`)}
            />
          </div>
          <Input
            label="Jednostka miary"
            placeholder="np. szt., mb, h, kpl."
            error={errs.custom_unit?.message}
            {...register(`line_items.${index}.custom_unit`)}
          />
          <Input
            label="Cena jedn. (PLN)"
            type="number"
            step="0.01"
            placeholder="0.00"
            error={errs.custom_unit_rate?.message}
            {...register(`line_items.${index}.custom_unit_rate`)}
          />
          <Input
            label="Ilość"
            type="number"
            step="any"
            error={errs.quantity?.message}
            {...register(`line_items.${index}.quantity`)}
          />
          <Input
            label="Opis (opcjonalny)"
            {...register(`line_items.${index}.description`)}
          />
          <div className="flex items-center gap-2 col-span-2">
            <input
              type="checkbox"
              id={`att_${index}`}
              {...register(`line_items.${index}.attachment_required`)}
              className="rounded"
            />
            <label htmlFor={`att_${index}`} className="text-sm text-gray-700 dark:text-gray-300">
              Załącznik obowiązkowy przy POCK
            </label>
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <input
              type="checkbox"
              id={`resfiles_${index}`}
              {...register(`line_items.${index}.result_files_expected`)}
              className="rounded"
            />
            <label htmlFor={`resfiles_${index}`} className="text-sm text-gray-700 dark:text-gray-300">
              Pliki pozycji realizacyjnej
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Wynikiem realizacji są pliki przesyłane przez podwykonawcę w zamówieniu
              </span>
            </label>
          </div>
        </div>
        <button type="button" onClick={onRemove} className="mt-6 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

// Wrapper — reads is_custom and renders the appropriate row
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function LineItemFormRow(props: LineItemFormRowProps) {
  const isCustom = useWatch({ control: props.control, name: `line_items.${props.index}.is_custom` });
  if (isCustom) return <CustomRow {...props} />;
  return <StandardRow {...props} />;
}
